import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AssignRowHint } from '@/pages/sale/components/assign-drag-grip';
import { Pin, PinOff, Reply } from 'lucide-react';

type CommentBubbleActionsProps = {
  isPinned: boolean;
  showDragHint?: boolean;
  tapAssignEnabled?: boolean;
  isSelected?: boolean;
  onPinToggle?: () => void;
  onReply: () => void;
};

export function CommentBubbleActions({
  isPinned,
  showDragHint = true,
  tapAssignEnabled = false,
  isSelected = false,
  onPinToggle,
  onReply,
}: CommentBubbleActionsProps) {
  return (
    <div className="flex shrink-0 items-center gap-0.5 sm:flex-col sm:items-end sm:gap-1">
      {onPinToggle && (
        <div className="flex items-center gap-1 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
          <IconButton title={isPinned ? 'Unpin' : 'Pin'} onClick={onPinToggle}>
            {isPinned ? (
              <PinOff className="h-3.5 w-3.5 text-blue-700 sm:h-4 sm:w-4" />
            ) : (
              <Pin className="h-3.5 w-3.5 text-gray-700 sm:h-4 sm:w-4" />
            )}
          </IconButton>
        </div>
      )}

      {showDragHint && (
        <AssignRowHint
          tapAssignEnabled={tapAssignEnabled}
          isSelected={isSelected}
          showDesktopHint
          showMobileHint
        />
      )}

      <IconButton title="Reply" onClick={onReply}>
        <Reply className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
      </IconButton>
    </div>
  );
}

function IconButton({
  title,
  onClick,
  children,
}: {
  title: string;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      variant="ghost"
      size="icon"
      title={title}
      onClick={onClick}
      onPointerDown={(event) => event.stopPropagation()}
      className="h-6 w-6 rounded-full hover:bg-gray-100 sm:h-7 sm:w-7"
    >
      {children}
    </Button>
  );
}

export function formatCommentDate(
  iso: string,
  compact?: boolean,
  fmtDateTime?: (iso: string) => string,
) {
  if (fmtDateTime) return fmtDateTime(iso);
  const date = new Date(iso);
  if (compact) {
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function commentBubbleClassName({
  isPinned,
  isNew,
  compact,
  dense,
}: {
  isPinned?: boolean;
  isNew?: boolean;
  compact?: boolean;
  dense?: boolean;
}) {
  return cn(
    'group relative flex w-full min-w-0 max-w-full items-start overflow-hidden border bg-white shadow-sm transition-all',
    dense
      ? 'gap-1 rounded-md px-1.5 py-0.5'
      : compact
        ? 'gap-1.5 rounded-lg px-1.5 py-0.5'
        : 'gap-2.5 rounded-xl px-2.5 py-1.5 sm:gap-3 sm:px-3 sm:py-2',
    'border-gray-200 hover:border-gray-300 hover:shadow-md hover:-translate-y-0.5',
    'cursor-grab active:cursor-grabbing',
    isPinned && 'border-blue-300 bg-blue-50',
    isNew && 'ring-2 ring-emerald-200/70',
  );
}
